import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import usePaymentConfig from '../../hooks/usePaymentConfig';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  FormControlLabel,
  Tooltip,
  Grid,
  Chip,
  Paper,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  FormGroup,
  Checkbox,
  FormHelperText,
} from '@mui/material';
import { CheckCircle, Cancel, Business, LocationOn, Send, Verified, OpenInNew, ErrorOutline, Percent, Payments } from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ProviderCompletenessBar, CHECK_TAB } from '../../components/Admin/shared/ProviderCompleteness';
import { useAuth } from '../../contexts/AuthContext';
import providerService from '../../services/providerService';
import adminService from '../../services/adminService';
import { trackProviderEditView, trackProviderSectionSave } from '../../services/analyticsService';
import categoryService from '../../services/categoryService';
import commissionService from '../../services/commissionService';
import { GOOGLE_MAPS_FRONTEND_API_KEY } from '../../config/api';
import { useLoadScript } from '@react-google-maps/api';
import { StandaloneSearchBox } from '@react-google-maps/api';

// HELYES IMPORTÁLÁSI ÚTVONALAK
import ProviderFormEditor from '../../components/Provider/ProviderFormEditor';
import PackageEditor from '../../components/Provider/PackageEditor';
import GalleryManager from '../Provider/GalleryManager'; // GalleryManager a pages/Provider mappában van
import { useConfirm } from '../../components/Common/ConfirmDialog';

const libraries = ["places"];

// Teljesség-tétel → alapadat-mező, amihez kattintáskor görgetünk
const CHECK_FIELD = { description: 'description', category: 'category-select', city: 'city', contact: 'contactEmail' };

// Automatikus mentés késleltetése az utolsó módosítás után
const AUTOSAVE_DELAY_MS = 1500;

// Magyar számlaszám: 2×8 vagy 3×8 számjegy (a backend ugyanezt ellenőrzi)
const BANK_ACCOUNT_REGEX = /^\d{8}-\d{8}(-\d{8})?$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Laza URL-ellenőrzés: a backend isURL()-ja a protokoll nélküli címet is elfogadja
const URL_REGEX = /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(:\d+)?(\/\S*)?$/i;

// Gépelés közben tagolja a számlaszámot: csak számjegyek, 8-asával kötőjel, max. 24 jegy
const formatBankAccount = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '').slice(0, 24);
  return digits.match(/.{1,8}/g)?.join('-') ?? '';
};

const toIntOrNull = (value) => (value === '' || value == null ? null : Number(value));

// Kliensoldali (azonnali) validáció — a backend szabályait tükrözi; hibás mezővel nem mentünk
function validateProvider(data, { isAdminUser, hasPackages }) {
  const errors = {};
  const name = (data.businessName || '').trim();
  if (!name) errors.businessName = 'A cégnév kötelező.';
  else if (name.length < 2 || name.length > 100) errors.businessName = 'A cégnév 2–100 karakter legyen.';

  if (data.website && !URL_REGEX.test(data.website.trim())) {
    errors.website = 'Érvénytelen webcím. Pl. https://pelda.hu';
  }
  if (data.contactEmail && !EMAIL_REGEX.test(data.contactEmail.trim())) {
    errors.contactEmail = 'Érvénytelen email cím.';
  }
  if (data.barionEmail && !EMAIL_REGEX.test(data.barionEmail.trim())) {
    errors.barionEmail = 'Érvénytelen email cím.';
  }

  if (!hasPackages) {
    const min = toIntOrNull(data.priceRangeMin);
    const max = toIntOrNull(data.priceRangeMax);
    if (min != null && (!Number.isInteger(min) || min < 0)) errors.priceRangeMin = 'Pozitív egész számot adjon meg.';
    if (max != null && (!Number.isInteger(max) || max < 0)) errors.priceRangeMax = 'Pozitív egész számot adjon meg.';
    if (!errors.priceRangeMin && !errors.priceRangeMax && min != null && max != null && max < min) {
      errors.priceRangeMax = 'A maximális ár nem lehet kisebb a minimálisnál.';
    }
  }

  if ((data.paymentMethods || []).includes('BANK_TRANSFER')) {
    if (!data.bankAccountNumber) errors.bankAccountNumber = 'Az online utalásos fizetéshez kötelező.';
    else if (!BANK_ACCOUNT_REGEX.test(data.bankAccountNumber)) {
      errors.bankAccountNumber = 'Hiányos számlaszám: 16 vagy 24 számjegy kell.';
    }
  }

  const address = (data.address || '').trim();
  if (address && (address.length < 5 || address.length > 100)) errors.address = 'A cím 5–100 karakter legyen.';
  const city = (data.city || '').trim();
  if (city && (city.length < 2 || city.length > 50)) errors.city = 'A város neve 2–50 karakter legyen.';
  if (data.zipCode && !/^\d{4}$/.test(String(data.zipCode).trim())) errors.zipCode = '4 számjegy, pl. 1234.';

  if (isAdminUser && data.customCommissionPercentage !== '' && data.customCommissionPercentage != null) {
    const pct = Number(data.customCommissionPercentage);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) errors.customCommissionPercentage = '0 és 100 közötti érték.';
  }
  return errors;
}

// Mezőnév → olvasható címke a mentési sávban
const FIELD_LABELS = {
  businessName: 'Cégnév',
  website: 'Weboldal',
  contactEmail: 'Kapcsolattartási email',
  barionEmail: 'Barion-tárca email',
  priceRangeMin: 'Minimális ár',
  priceRangeMax: 'Maximális ár',
  bankAccountNumber: 'Bankszámlaszám',
  address: 'Utca, házszám',
  city: 'Város',
  zipCode: 'Irányítószám',
  customCommissionPercentage: 'Egyedi jutalék %',
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`provider-edit-tabpanel-${index}`}
      aria-labelledby={`provider-edit-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function SectionCard({ title, icon, children }) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {icon && React.cloneElement(icon, { sx: { mr: 1, color: 'primary.main' } })}
          <Typography variant="h6" color="primary.dark">{title}</Typography>
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

// Az alapadatok fül alján ragadó sáv: mutatja az automatikus mentés állapotát, és kézi
// mentést / a hibás mezőhöz ugrást ad (a gomb az űrlap submitja)
function SaveStatusBar({ state, errorKeys, onSubmitLabel }) {
  const STATUS = {
    idle: { icon: <CheckCircle fontSize="small" color="success" />, text: 'Automatikus mentés aktív', color: 'text.secondary' },
    saved: { icon: <CheckCircle fontSize="small" color="success" />, text: 'Minden módosítás mentve', color: 'success.main' },
    dirty: { icon: <CircularProgress size={16} />, text: 'Mentés hamarosan…', color: 'text.secondary' },
    saving: { icon: <CircularProgress size={16} />, text: 'Mentés…', color: 'text.secondary' },
    invalid: {
      icon: <ErrorOutline fontSize="small" color="warning" />,
      text: `Nincs mentve: ${errorKeys.map(k => FIELD_LABELS[k] || k).join(', ')}`,
      color: 'warning.main',
    },
    error: { icon: <Cancel fontSize="small" color="error" />, text: 'A mentés nem sikerült.', color: 'error.main' },
  };
  const current = STATUS[state] || STATUS.idle;
  // Gomb csak akkor, ha van teendő (függő/hibás/sikertelen mentés) — különben csak az állapot látszik
  const showButton = state === 'dirty' || state === 'invalid' || state === 'error';
  return (
    <Paper
      elevation={3}
      sx={{
        position: 'sticky',
        bottom: { xs: 24, sm: 24 },
        zIndex: 5,
        mt: 3,
        // A jobb alsó lebegő visszajelzés-gomb (56px + 24px margó) ne takarja ki a sáv gombját
        mr: { xs: 8, sm: 7, lg: 0 },
        px: 2,
        py: 1,
        minHeight: 56,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        border: '1px solid',
        borderColor: state === 'invalid' ? 'warning.light' : state === 'error' ? 'error.light' : 'divider',
      }}
      role="status"
      aria-live="polite"
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', flexShrink: 0 }}>{current.icon}</Box>
        <Typography variant="body2" color={current.color} sx={{ overflowWrap: 'anywhere' }}>
          {current.text}
        </Typography>
      </Box>
      {showButton && (
        <Button
          type="submit"
          size="small"
          variant={state === 'dirty' ? 'outlined' : 'contained'}
          color={state === 'invalid' ? 'warning' : 'primary'}
          sx={{ flexShrink: 0, minHeight: 40, whiteSpace: 'nowrap' }}
        >
          {state === 'error' ? 'Újra' : onSubmitLabel}
        </Button>
      )}
    </Paper>
  );
}

const ProviderEditPage = () => {
  const [confirm, confirmDialog] = useConfirm();
  const { id: urlId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Átirányításkor kapott üzenet (pl. meghívó beváltása után, függő email-csere)
  const [flashMessage, setFlashMessage] = useState(location.state?.message || '');
  const { user, isAuthenticated, isAdmin, isProvider } = useAuth();
  const paymentConfig = usePaymentConfig();
  const id = urlId ?? user?.provider?.id?.toString();

  const [providerData, setProviderData] = useState(null);
  const [completeness, setCompleteness] = useState(null);
  // Mentés / galéria-mentés után nő — ekkor kérjük le újra a teljességet
  const [completenessTick, setCompletenessTick] = useState(0);
  const galleryWasDirtyRef = useRef(false);
  // Stabil callback: a teljességet csak akkor kérjük le újra, ha a galéria piszkosból mentett lett
  const handleGalleryDirtyChange = useCallback((dirty) => {
    if (galleryWasDirtyRef.current && !dirty) setCompletenessTick(t => t + 1);
    galleryWasDirtyRef.current = dirty;
  }, []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [backendErrors, setBackendErrors] = useState({});

  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [commissionGroups, setCommissionGroups] = useState([]);

  // Google Maps Search Box
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_FRONTEND_API_KEY,
    libraries: libraries,
  });
  const searchBoxRef = useRef(null);

  // ?tab=N: közvetlenül egy fülre nyit (pl. a dashboard teendőlistájáról)
  const [tabValue, setTabValue] = useState(() => {
    const tab = parseInt(new URLSearchParams(location.search).get('tab'), 10);
    return tab >= 0 && tab <= 3 ? tab : 0;
  });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [galleryInitialTab, setGalleryInitialTab] = useState(0);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Azonnali validáció: egy mező hibája az első elhagyása (blur) után látszik,
  // a "Hibák mutatása" / mentés gomb után pedig mindegyiké
  const [touched, setTouched] = useState({});
  const [showAllErrors, setShowAllErrors] = useState(false);

  // Automatikus mentés: minden módosítás növeli az editVersion-t; ha nincs hiba, késleltetve mentünk.
  // saveState: 'idle' | 'dirty' | 'invalid' | 'saving' | 'saved' | 'error'
  const [saveState, setSaveState] = useState('idle');
  const editVersionRef = useRef(0);
  const savedVersionRef = useRef(0);
  const savingRef = useRef(false);
  const saveAgainRef = useRef(false);
  const autosaveTimerRef = useRef(null);
  const latestRef = useRef({});
  const saveTrackedRef = useRef(false);

  const fetchProviderData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const response = await providerService.getProviderDetails(id);
      if (response.success) {
        // MÓDOSÍTVA: response.data.provider-t mentjük el
        const fetchedProvider = response.data.provider;
        // Csendes frissítésnél (pl. beküldés után) a még nem mentett módosításokat nem írjuk felül
        if (silent && editVersionRef.current !== savedVersionRef.current) {
          setProviderData(prev => ({ ...prev, status: fetchedProvider.status, slug: fetchedProvider.slug, barionStatus: fetchedProvider.barionStatus }));
          setCompletenessTick(t => t + 1);
          return;
        }
        setProviderData(fetchedProvider);
        // Teljes újratöltés után a betöltött állapot számít mentettnek
        savedVersionRef.current = editVersionRef.current;
        setSaveState('idle');
        setCompletenessTick(t => t + 1);
        const initCats = fetchedProvider.providerCategories?.length > 0
          ? fetchedProvider.providerCategories.map(pc => pc.categoryId)
          : (fetchedProvider.categoryId ? [fetchedProvider.categoryId] : []);
        setSelectedCategories(initCats);
      } else {
        setError(response.message || 'Szolgáltató adatainak betöltése sikertelen.');
      }
    } catch (err) {
      console.error('Error fetching provider data:', err);
      setError('Hiba történt a szolgáltató adatainak betöltése során.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id])

  const fetchCategories = useCallback(async () => {
    try {
      const response = await categoryService.getCategories();
      if (response.success) {
        setCategories(response.data.categories);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  const fetchCommissionGroups = useCallback(async () => {
    try {
      const response = await commissionService.getCommissionGroups();
      if (response.success) {
        setCommissionGroups(response.data);
      }
    } catch (err) {
      console.error('Error fetching commission groups:', err);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || (!isAdmin() && !isProvider())) {
      navigate('/login');
      return;
    }
    // Ha szolgáltató, ellenőrizzük, hogy a saját profilját szerkeszti-e
    // Adminok szerkeszthetik bármely szolgáltatót
    if (isProvider() && !isAdmin() && user?.provider?.id && user.provider.id !== parseInt(id)) {
      navigate('/dashboard');
      return;
    }

    fetchProviderData();
    fetchCategories();
    if (isAdmin && isAdmin()) fetchCommissionGroups();
    trackProviderEditView(id);
  }, [isAuthenticated, isAdmin, isProvider, user, id, navigate, fetchProviderData, fetchCategories, fetchCommissionGroups]);

  const markDirty = () => {
    editVersionRef.current += 1;
    setSaveState('dirty');
  };

  // Minden alapadat-módosítás ezen megy át: állapot + backend-hiba törlése + automatikus mentés jelzése
  const updateFields = (changes) => {
    setProviderData((prev) => ({ ...prev, ...changes }));
    setBackendErrors(prev => {
      const keys = Object.keys(changes).filter(k => prev[k]);
      if (keys.length === 0) return prev;
      const next = { ...prev };
      keys.forEach(k => delete next[k]);
      return next;
    });
    markDirty();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    updateFields({ [name]: type === 'checkbox' ? checked : value });
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    if (name) setTouched(prev => (prev[name] ? prev : { ...prev, [name]: true }));
  };

  const handleCategoryChange = (e) => {
    const val = e.target.value;
    setSelectedCategories(val);
    updateFields({ categoryIds: val });
  };

  const togglePaymentMethod = (method, checked) => {
    const current = providerData.paymentMethods || [];
    updateFields({
      paymentMethods: checked ? [...current, method] : current.filter((m) => m !== method),
    });
  };

  const handlePlaceSelect = () => {
    if (searchBoxRef.current && searchBoxRef.current.getPlaces) {
      const places = searchBoxRef.current.getPlaces();
      if (places && places.length > 0) {
        const place = places[0];
        const addressComponents = place.address_components;
        let city = '';
        let zipCode = '';
        let address = '';
        let county = '';

        addressComponents.forEach(component => {
          if (component.types.includes('locality')) {
            city = component.long_name;
          }
          if (component.types.includes('postal_code')) {
            zipCode = component.long_name;
          }
          if (component.types.includes('route')) {
            address = component.long_name;
          }
          if (component.types.includes('street_number')) {
            address += ` ${component.long_name}`;
          }
          if (component.types.includes('administrative_area_level_1')) {
            county = component.long_name;
          }
        });

        updateFields({
          city: city,
          zipCode: zipCode,
          address: address,
          county: county,
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng(),
        });
        setTouched(prev => ({ ...prev, city: true, zipCode: true, address: true }));
      }
    }
  };

  const isAdminUser = !!(isAdmin && isAdmin());
  const hasPackages = (providerData?.packages?.length ?? 0) > 0;
  const clientErrors = useMemo(
    () => (providerData ? validateProvider(providerData, { isAdminUser, hasPackages }) : {}),
    [providerData, isAdminUser, hasPackages]
  );
  const errorKeys = Object.keys(clientErrors);

  // A mentéshez mindig a legfrissebb állapotot olvassuk (időzítőből / unmountkor is)
  latestRef.current = { providerData, selectedCategories, clientErrors };

  const buildPayload = (data, categoryIds) => ({
    businessName: (data.businessName || '').trim(),
    description: data.description,
    shortDescription: data.shortDescription || null,
    city: data.city,
    county: data.county,
    zipCode: data.zipCode,
    address: data.address,
    website: data.website?.trim() || null,
    priceRangeMin: toIntOrNull(data.priceRangeMin),
    priceRangeMax: toIntOrNull(data.priceRangeMax),
    categoryIds: categoryIds.map(cid => parseInt(cid)),
    paymentMethods: Array.isArray(data.paymentMethods) ? data.paymentMethods : [],
    bankAccountNumber: data.bankAccountNumber || null,
    contactEmail: data.contactEmail?.trim() || null,
    barionEmail: data.barionEmail?.trim() || null,
    ...(isAdminUser && data.barionStatus ? { barionStatus: data.barionStatus } : {}),
    commissionGroupId: toIntOrNull(data.commissionGroupId),
    customCommissionPercentage: toIntOrNull(data.customCommissionPercentage),
    customCommissionFixedAmount: toIntOrNull(data.customCommissionFixedAmount),
  });

  // Egyszerre csak egy mentés fut; ha közben újabb módosítás jön, utána újra mentünk.
  // A válasszal NEM írjuk felül az űrlapot (a felhasználó közben gépelhetett), csak a szerver által
  // számolt mezőket vesszük át.
  const saveNow = async () => {
    if (savingRef.current) {
      saveAgainRef.current = true;
      return;
    }
    const { providerData: data, selectedCategories: cats, clientErrors: errs } = latestRef.current;
    if (!data || Object.keys(errs).length > 0) return;
    if (editVersionRef.current === savedVersionRef.current) return;

    const version = editVersionRef.current;
    savingRef.current = true;
    setSaveState('saving');
    try {
      const response = await providerService.updateProvider(id, buildPayload(data, cats));
      if (!response.success) throw new Error(response.message || 'Hiba történt az adatok mentése során.');
      const saved = response.data?.provider;
      if (saved) {
        setProviderData(prev => ({
          ...prev,
          slug: saved.slug,
          status: saved.status,
          barionStatus: saved.barionStatus,
          latitude: saved.latitude,
          longitude: saved.longitude,
        }));
      }
      savedVersionRef.current = version;
      setCompletenessTick(t => t + 1);
      if (!saveTrackedRef.current) {
        trackProviderSectionSave(id, 'alap_adatok');
        saveTrackedRef.current = true;
      }
      setSaveState(editVersionRef.current === version ? 'saved' : 'dirty');
    } catch (err) {
      console.error('Error updating provider:', err);
      if (err.details?.length > 0) {
        const fieldErrors = {};
        err.details.forEach(e => {
          const key = e.path?.startsWith('categoryIds') ? 'categoryIds' : e.path;
          if (key) fieldErrors[key] = e.msg;
        });
        setBackendErrors(fieldErrors);
        setShowAllErrors(true);
      }
      setSaveState('error');
      showSnackbar(err.message || 'A mentés nem sikerült.', 'error');
    } finally {
      savingRef.current = false;
      if (saveAgainRef.current) {
        saveAgainRef.current = false;
        saveNow();
      }
    }
  };
  const saveNowRef = useRef(saveNow);
  saveNowRef.current = saveNow;

  // Késleltetett automatikus mentés minden módosítás után — hibás mezővel nem mentünk
  useEffect(() => {
    if (!providerData || editVersionRef.current === savedVersionRef.current) return undefined;
    if (errorKeys.length > 0) {
      setSaveState(s => (s === 'saving' ? s : 'invalid'));
      return undefined;
    }
    setSaveState(s => (s === 'invalid' ? 'dirty' : s));
    autosaveTimerRef.current = setTimeout(() => saveNowRef.current(), AUTOSAVE_DELAY_MS);
    return () => clearTimeout(autosaveTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerData, selectedCategories, errorKeys.length]);

  // Oldal elhagyásakor: függő módosítást azonnal mentünk, böngésző-bezáráskor figyelmeztetünk
  const hasUnsaved = saveState === 'dirty' || saveState === 'invalid' || saveState === 'saving' || saveState === 'error';
  useEffect(() => {
    if (!hasUnsaved) return undefined;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsaved]);
  useEffect(() => () => {
    clearTimeout(autosaveTimerRef.current);
    saveNowRef.current();
  }, []);

  // "Mentés most" / "Hibák mutatása": minden hibát láthatóvá teszünk és az elsőhöz görgetünk
  const handleSubmit = (e) => {
    e.preventDefault();
    if (errorKeys.length > 0) {
      setShowAllErrors(true);
      const first = document.querySelector(`form [name="${errorKeys[0]}"]`);
      if (first) {
        first.scrollIntoView({ behavior: 'smooth', block: 'center' });
        first.focus({ preventScroll: true });
      }
      return;
    }
    clearTimeout(autosaveTimerRef.current);
    saveNow();
  };

  // Egy mező hibája: a backend hibája mindig, a kliensoldali az első elhagyás után
  const fieldError = (name) =>
    backendErrors[name] || ((touched[name] || showAllErrors) ? clientErrors[name] : undefined);
  const errorProps = (name, helper) => {
    const err = fieldError(name);
    return { error: !!err, helperText: err || helper, onBlur: handleBlur };
  };

  const handleSendInvite = async () => {
    setInviteLoading(true);
    try {
      await adminService.sendProviderInvite(id);
      showSnackbar('Meghívó email elküldve.', 'success');
      fetchProviderData({ silent: true });
    } catch (err) {
      showSnackbar(err?.message || 'Hiba a meghívó küldése során.', 'error');
    } finally {
      setInviteLoading(false);
    }
  };

  // Ellenőrzési szempontok (képek, ár, leírás, ...) amíg a profil nincs élesítve
  const providerStatus = providerData?.status;
  useEffect(() => {
    if (!id || !['DRAFT', 'PENDING'].includes(providerStatus)) {
      setCompleteness(null);
      return;
    }
    providerService.getCompleteness(id)
      .then(setCompleteness)
      .catch(() => setCompleteness(null));
  }, [id, providerStatus, completenessTick]);

  const handleConfirmProvider = async () => {
    const missingNote = completeness && !completeness.ready
      ? `

Még hiányzik: ${completeness.missing.join(', ')}. A hiányos profilok jóváhagyása tovább tarthat.`
      : '';
    if (!(await confirm({ title: 'Beküldés jóváhagyásra', message: `Beküldi a profilját jóváhagyásra? Rövid ellenőrzés után jelenik meg a keresésben.${missingNote}`, confirmLabel: 'Beküldés' }))) {
      return;
    }
    setConfirmLoading(true);
    try {
      // A még függő alapadat-módosítást előbb elmentjük, hogy a beküldött profil a friss legyen
      clearTimeout(autosaveTimerRef.current);
      await saveNow();
      await providerService.confirmProvider(id);
      showSnackbar('Profilját beküldte jóváhagyásra. Ellenőrzés után megjelenik a keresésben.', 'success');
      fetchProviderData({ silent: true });
    } catch (err) {
      showSnackbar(err?.message || 'Hiba a profil beküldése során.', 'error');
    } finally {
      setConfirmLoading(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Fülváltáskor nem kérdezünk: a galéria és az alapadatok is automatikusan mentenek (a galéria
  // a fül elhagyásakor azonnal elmenti a függő módosítást)
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // A fül az URL-ben marad, így frissítés után ugyanott nyílik. Szándékosan nem navigate():
    // az a router location-jét is váltaná, a betöltő effekt (navigate a függősége) újrafutna, és
    // az egész oldal újratöltődne — elveszítve a még nem mentett módosításokat.
    const params = new URLSearchParams(window.location.search);
    params.set('tab', String(newValue));
    window.history.replaceState(window.history.state, '', `${window.location.pathname}?${params.toString()}`);
  };

  // Teljesség-tétel kattintás: a megfelelő fülre visz, alapadatnál a mezőhöz görget
  const handleCheckClick = (check) => {
    const tab = CHECK_TAB[check.key] ?? 0;
    if (tab === 2) setGalleryInitialTab(check.key === 'profileImage' ? 1 : 0);
    handleTabChange(null, tab);
    const field = CHECK_FIELD[check.key];
    if (tab === 0 && field) {
      setTimeout(() => {
        const el = document.querySelector(`form [name="${field}"]`) || document.getElementById(field);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.focus?.({ preventScroll: true });
        }
      }, 50);
    }
  };

  if (loading || !isLoaded) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Adatok betöltése...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!providerData) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">Szolgáltató nem található.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ pt: { xs: 3, sm: 4 }, pb: { xs: 12, sm: 10, lg: 4 } }}>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' }, overflowWrap: 'anywhere' }}>
        {isAdmin() ? `Szolgáltató szerkesztése: ${providerData.businessName}` : 'Szolgáltatói profil szerkesztése'}
      </Typography>

      {flashMessage && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setFlashMessage('')}>
          {flashMessage}
        </Alert>
      )}

      {providerData.status === 'DRAFT' && isAdmin() && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Ez a profil még piszkozat, nem látja a szolgáltató. Ha kész a kitöltéssel, küldd ki a meghívó emailt.
          <Box sx={{ mt: 1.5 }}>
            <Button
              variant="outlined"
              color="info"
              size="small"
              startIcon={inviteLoading ? <CircularProgress size={16} /> : <Send fontSize="small" />}
              disabled={inviteLoading}
              onClick={handleSendInvite}
              sx={{ minHeight: 40 }}
            >
              Meghívó küldése
            </Button>
          </Box>
        </Alert>
      )}

      {providerData.status === 'DRAFT' && !isAdmin() && isProvider() && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Tekintse át és egészítse ki az alábbi adatokat. Ha mindent rendben talál, küldje be jóváhagyásra — rövid ellenőrzés után a profilja megjelenik a keresésben.
          <Box sx={{ mt: 1.5 }}>
            <Button
              variant="contained"
              startIcon={confirmLoading ? <CircularProgress size={16} color="inherit" /> : <Verified fontSize="small" />}
              disabled={confirmLoading}
              onClick={handleConfirmProvider}
              sx={{ minHeight: 44 }}
            >
              Beküldöm jóváhagyásra
            </Button>
          </Box>
        </Alert>
      )}

      {providerData.status === 'PENDING' && !isAdmin() && isProvider() && (
        <Alert severity="info" sx={{ mb: 2 }}>
          A profilod jóváhagyásra vár. Ellenőrzés után megjelenik a keresésben — addig is szerkesztheted.
        </Alert>
      )}

      {completeness && <ProviderCompletenessBar completeness={completeness} onCheckClick={handleCheckClick} />}

      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="provider edit tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ minWidth: 0, flex: 1, '& .MuiTabs-scrollButtons': { width: 28 } }}
        >
          <Tab label="Alapadatok" id="provider-edit-tab-0" aria-controls="provider-edit-tabpanel-0" />
          <Tab label="Ajánlatkérő űrlap" id="provider-edit-tab-1" aria-controls="provider-edit-tabpanel-1" />
          <Tab label="Galéria" id="provider-edit-tab-2" aria-controls="provider-edit-tabpanel-2" />
          <Tab label="Csomagok" id="provider-edit-tab-3" aria-controls="provider-edit-tabpanel-3" />
        </Tabs>
        <Tooltip title="Profil megtekintése (új lapon)">
          <Button
            variant="outlined"
            size="small"
            aria-label="Profil megtekintése új lapon"
            onClick={() => window.open(`${window.location.origin}/szolgaltato/${providerData.slug || providerData.id}`, '_blank', 'noopener,noreferrer')}
            sx={{ whiteSpace: 'nowrap', flexShrink: 0, minHeight: 40, minWidth: { xs: 40, sm: 'auto' }, px: { xs: 1, sm: 1.5 } }}
          >
            <OpenInNew fontSize="small" />
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, ml: 1 }}>Profil megtekintése</Box>
          </Button>
        </Tooltip>
      </Box>

      {/* Tab Panelek */}
      <TabPanel value={tabValue} index={0}>
        <Box component="form" noValidate onSubmit={handleSubmit}>
          <SectionCard title="Alapadatok" icon={<Business />}>
            <Grid container columnSpacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Cégnév"
                  name="businessName"
                  value={providerData.businessName || ''}
                  onChange={handleChange}
                  margin="normal"
                  required
                  inputProps={{ maxLength: 100 }}
                  {...errorProps('businessName')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth margin="normal" error={!!backendErrors.categoryIds}>
                  <InputLabel id="category-label">Kategóriák</InputLabel>
                  <Select
                    labelId="category-label"
                    id="category-select"
                    multiple
                    value={selectedCategories}
                    onChange={handleCategoryChange}
                    label="Kategóriák"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map(id => {
                          const cat = categories.find(c => c.id === id);
                          return cat ? <Chip key={id} label={cat.name} size="small" /> : null;
                        })}
                      </Box>
                    )}
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id} sx={{ gap: 1 }}>
                        <Checkbox
                          checked={selectedCategories.includes(cat.id)}
                          size="small"
                          sx={{ p: 0 }}
                        />
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {backendErrors.categoryIds && (
                    <FormHelperText>{backendErrors.categoryIds}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Rövid leírás"
                  name="shortDescription"
                  value={providerData.shortDescription || ''}
                  onChange={handleChange}
                  margin="normal"
                  multiline
                  rows={2}
                  inputProps={{ maxLength: 300 }}
                  {...errorProps('shortDescription', `${(providerData.shortDescription || '').length}/300 – kártyákon és a profil tetején jelenik meg`)}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Leírás"
                  name="description"
                  value={providerData.description || ''}
                  onChange={handleChange}
                  margin="normal"
                  multiline
                  minRows={4}
                  maxRows={14}
                  inputProps={{ maxLength: 5000 }}
                  {...errorProps('description', `${(providerData.description || '').length}/5000 – a profiloldal bemutatkozó részében jelenik meg`)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Weboldal"
                  name="website"
                  value={providerData.website || ''}
                  onChange={handleChange}
                  margin="normal"
                  placeholder="https://pelda.hu"
                  inputProps={{ inputMode: 'url' }}
                  {...errorProps('website')}
                />
              </Grid>
            </Grid>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3 }}>
              Ársáv
            </Typography>
            <Grid container columnSpacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Minimális ár (Ft)"
                  name="priceRangeMin"
                  type="number"
                  value={providerData.priceRangeMin ?? ''}
                  onChange={handleChange}
                  margin="normal"
                  inputProps={{ min: 0 }}
                  disabled={hasPackages}
                  {...errorProps('priceRangeMin', hasPackages ? 'A csomagárakból számolva' : undefined)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Maximális ár (Ft)"
                  name="priceRangeMax"
                  type="number"
                  value={providerData.priceRangeMax ?? ''}
                  onChange={handleChange}
                  margin="normal"
                  inputProps={{ min: 0 }}
                  disabled={hasPackages}
                  {...errorProps('priceRangeMax', hasPackages ? 'A csomagárakból számolva' : undefined)}
                />
              </Grid>
            </Grid>
          </SectionCard>

          {/* Jutalékcsoport – csak admin látja */}
          {isAdmin && isAdmin() && (
            <SectionCard title="Jutalékcsoport és jutalék beállítások" icon={<Percent />}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Jutalékcsoport</InputLabel>
                    <Select
                      name="commissionGroupId"
                      value={providerData.commissionGroupId || ''}
                      label="Jutalékcsoport"
                      onChange={(e) => updateFields({ commissionGroupId: e.target.value || null })}
                    >
                      <MenuItem value="">
                        <em>Nincs csoport (egyedi beállítás)</em>
                      </MenuItem>
                      {commissionGroups.filter(g => g.isActive).map(group => (
                        <MenuItem key={group.id} value={group.id}>
                          {group.name} ({parseFloat(group.percentage || 0)}% + {parseFloat(group.fixedAmount || 0).toLocaleString('hu-HU')} Ft fix)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    fullWidth
                    label="Egyedi jutalék %"
                    name="customCommissionPercentage"
                    type="number"
                    value={providerData.customCommissionPercentage || ''}
                    onChange={handleChange}
                    margin="normal"
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    {...errorProps('customCommissionPercentage', 'Felülírja a csoport %')}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    fullWidth
                    label="Egyedi fix jutalék (Ft)"
                    name="customCommissionFixedAmount"
                    type="number"
                    value={providerData.customCommissionFixedAmount || ''}
                    onChange={handleChange}
                    margin="normal"
                    inputProps={{ min: 0 }}
                    helperText="Felülírja a csoport fixet"
                  />
                </Grid>
              </Grid>
              {providerData.commissionGroupId && (
                <Typography variant="caption" color="text.secondary">
                  Ha egyedi jutalék van megadva, az felülírja a csoport beállítását.
                </Typography>
              )}
            </SectionCard>
          )}

          <SectionCard title="Fizetési módok" icon={<Payments />}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Adja meg, milyen fizetési módokat fogad el az ügyfelektől. Ezek közül választhatnak fizetéskor.
            </Typography>
            <FormGroup>
              {[
                { value: 'ON_SITE_CARD', label: 'Helyszínen bankkártyával' },
                { value: 'ON_SITE_CASH', label: 'Helyszínen készpénzzel' },
                { value: 'BANK_TRANSFER', label: 'Online utalással (számlaszámra)' },
              ].map((method) => (
                <FormControlLabel
                  key={method.value}
                  control={
                    <Checkbox
                      checked={(providerData.paymentMethods || []).includes(method.value)}
                      onChange={(e) => togglePaymentMethod(method.value, e.target.checked)}
                    />
                  }
                  label={method.label}
                />
              ))}
              {paymentConfig.barionEnabled ? (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(providerData.paymentMethods || []).includes('BARION')}
                      onChange={(e) => togglePaymentMethod('BARION', e.target.checked)}
                    />
                  }
                  label="Barion (online bankkártyás fizetés)"
                />
              ) : (
                <FormControlLabel
                  control={<Checkbox checked={false} disabled />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>Barion (online fizetés)</span>
                      <Chip label="Hamarosan" size="small" color="info" variant="outlined" />
                    </Box>
                  }
                />
              )}
            </FormGroup>
            {paymentConfig.barionEnabled && (providerData.paymentMethods || []).includes('BARION') && (
              <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, maxWidth: 620 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2">Barion-tárca</Typography>
                  <Chip
                    size="small"
                    label={{ VERIFIED: 'Ellenőrizve — online fizetés aktív', DISABLED: 'Letiltva', PENDING: 'Ellenőrzésre vár' }[providerData.barionStatus] || 'Ellenőrzésre vár'}
                    color={{ VERIFIED: 'success', DISABLED: 'error' }[providerData.barionStatus] || 'warning'}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" component="div">
                  Az online fizetésnél az ügyfél díja (a platform jutalékának levonásával) közvetlenül az Ön Barion-tárcájába érkezik. Beállítás:
                  <ol style={{ margin: '8px 0', paddingLeft: 20 }}>
                    <li>
                      Regisztráljon <strong>céges / egyéni vállalkozói</strong> Barion-tárcát:{' '}
                      <a href="https://www.barion.com/hu/" target="_blank" rel="noopener noreferrer">barion.com</a>
                    </li>
                    <li>Végezze el a Barionnál a személyazonosság- és cégadat-ellenőrzést (enélkül a tárca nem fogadhat pénzt).</li>
                    <li>Adja meg lent a tárcához tartozó email címet — a változás automatikusan mentődik.</li>
                    <li>Ellenőrzés után (általában 1–2 munkanap) az ügyfelei online is fizethetnek Önnél.</li>
                  </ol>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  label="Barion-tárca email címe"
                  name="barionEmail"
                  type="email"
                  value={providerData.barionEmail || ''}
                  onChange={handleChange}
                  inputProps={{ inputMode: 'email' }}
                  {...errorProps('barionEmail', 'Az email cím módosítása után a tárcát újra ellenőrizzük.')}
                  sx={{ mt: 1 }}
                />
                {isAdmin() && (
                  <FormControl size="small" sx={{ mt: 2, minWidth: 260 }}>
                    <InputLabel>Barion státusz (admin)</InputLabel>
                    <Select
                      label="Barion státusz (admin)"
                      value={providerData.barionStatus || 'PENDING'}
                      onChange={(e) => updateFields({ barionStatus: e.target.value })}
                    >
                      <MenuItem value="PENDING">Ellenőrzésre vár</MenuItem>
                      <MenuItem value="VERIFIED">Ellenőrizve (online fizetés aktív)</MenuItem>
                      <MenuItem value="DISABLED">Letiltva</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Box>
            )}
            {(providerData.paymentMethods || []).includes('BANK_TRANSFER') && (
              <TextField
                fullWidth
                label="Bankszámlaszám"
                name="bankAccountNumber"
                value={providerData.bankAccountNumber || ''}
                onChange={(e) => updateFields({ bankAccountNumber: formatBankAccount(e.target.value) })}
                margin="normal"
                required
                placeholder="pl. 11773315-00405010-12345678"
                {...errorProps('bankAccountNumber', '16 vagy 24 számjegy — a kötőjeleket automatikusan beírjuk.')}
                sx={{ maxWidth: 420 }}
                inputProps={{ maxLength: 26, inputMode: 'numeric', autoComplete: 'off' }}
              />
            )}
          </SectionCard>

          <SectionCard title="Elérhetőség" icon={<LocationOn />}>
            <TextField
              fullWidth
              label="Kapcsolattartási email"
              name="contactEmail"
              type="email"
              value={providerData.contactEmail || ''}
              onChange={handleChange}
              margin="normal"
              placeholder={providerData.user?.email || 'pl. kapcsolat@vallalkozas.hu'}
              inputProps={{ inputMode: 'email' }}
              {...errorProps('contactEmail', 'Ez az email jelenik meg a profilján. Ha üres, a bejelentkezési email szerepel.')}
              sx={{ mb: 2 }}
            />
            {isLoaded && (
              <Box sx={{ mb: 2 }}>
                <StandaloneSearchBox
                  onLoad={(ref) => (searchBoxRef.current = ref)}
                  onPlacesChanged={handlePlaceSelect}
                >
                  <TextField
                    fullWidth
                    label="Cím keresése (automatikus kitöltés)"
                    variant="outlined"
                    margin="normal"
                    placeholder="Kezdje el gépelni a címet..."
                    helperText="A kiválasztott cím automatikusan kitölti az alábbi mezőket"
                  />
                </StandaloneSearchBox>
              </Box>
            )}
            <Grid container columnSpacing={2}>
              <Grid size={{ xs: 4, sm: 3 }}>
                <TextField
                  fullWidth
                  label="Irányítószám"
                  name="zipCode"
                  value={providerData.zipCode || ''}
                  onChange={handleChange}
                  margin="normal"
                  inputProps={{ maxLength: 4, inputMode: 'numeric' }}
                  {...errorProps('zipCode', '4 számjegy')}
                />
              </Grid>
              <Grid size={{ xs: 8, sm: 9 }}>
                <TextField
                  fullWidth
                  label="Város"
                  name="city"
                  value={providerData.city || ''}
                  onChange={handleChange}
                  margin="normal"
                  {...errorProps('city')}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Utca, házszám"
                  name="address"
                  value={providerData.address || ''}
                  onChange={handleChange}
                  margin="normal"
                  {...errorProps('address')}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Megye"
                  name="county"
                  value={providerData.county || ''}
                  onChange={handleChange}
                  margin="normal"
                  {...errorProps('county')}
                />
              </Grid>
            </Grid>
          </SectionCard>

          <SaveStatusBar
            state={saveState}
            errorKeys={errorKeys}
            onSubmitLabel={errorKeys.length > 0 ? 'Javítás' : 'Mentés most'}
          />
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <ProviderFormEditor providerId={parseInt(id)} />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <GalleryManager
          providerId={parseInt(id)}
          embedded={true}
          initialTab={galleryInitialTab}
          onDirtyChange={handleGalleryDirtyChange}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <PackageEditor providerId={parseInt(id)} />
      </TabPanel>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        // A profilszerkesztő minden fülén ugyanott: alul középen, a mentési sáv és a lebegő gomb fölött
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 96, sm: 104 } }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {confirmDialog}
    </Container>
  );
};

export default ProviderEditPage;